import { useEffect, useState } from 'react';

/** En rad i sonden: id-en og tiden lista ordnes etter i «Nyest først». */
export interface SondeRad {
  id: string;
  tid: string;
}

/** Taket på sonden. Flere nye enn dette sies som «20+», aldri som et tall vi ikke vet. */
export const NYE_TAK = 20;

/** Pollingen for nye rader og tellerne: ett lite kall i minuttet mens fanen er synlig (DD-74). */
export const POLL_MS = 60_000;

const nyeste = (rader: readonly SondeRad[]) => rader.reduce((m, r) => Math.max(m, Date.parse(r.tid)), 0);

/**
 * Hvor mange rader som har kommet inn siden lista sist ble hentet (DD-74, M4.12).
 *
 * Grensen settes hver gang lista hentes: den nyeste tiden i lista eller i en sonde som er minst
 * like fersk som lista. I «Frist først» står de nye nederst, ofte utenfor første side, så lista
 * alene vet ikke hva som er nyest — derfor sonden. Det som kommer inn i millisekundene mellom
 * lista og sonden telles ikke; det står i lista neste gang den hentes.
 *
 * Tidene er tjenerens, aldri klientens klokke, så en skjev klokke gir ikke falske «nye».
 */
export function useNyeSiden(liste: { rader: readonly SondeRad[]; hentet: number }, sonde: { rader: readonly SondeRad[] | undefined; hentet: number }, aktiv: boolean): { antall: number; flere: boolean } {
  const [grense, setGrense] = useState<{ hentet: number; tid: number } | null>(null);

  useEffect(() => {
    if (!aktiv || !sonde.rader || liste.hentet === 0) return;
    if (grense?.hentet === liste.hentet) return;
    if (sonde.hentet < liste.hentet) return;
    setGrense({ hentet: liste.hentet, tid: Math.max(nyeste(liste.rader), nyeste(sonde.rader)) });
  }, [aktiv, sonde.rader, sonde.hentet, liste.hentet, liste.rader, grense]);

  if (!aktiv || !grense || grense.hentet !== liste.hentet || !sonde.rader) return { antall: 0, flere: false };
  const antall = sonde.rader.filter((r) => Date.parse(r.tid) > grense.tid).length;
  return { antall, flere: antall >= NYE_TAK };
}

/** Filteret uten rekkefølgen: sonden er alltid «nyest», og nøkkelen skal være den samme i begge. */
export function utenRekkefolge<T extends { sort?: unknown }>(filter: T): Omit<T, 'sort'> {
  const kopi = { ...filter };
  delete kopi.sort;
  return kopi;
}
